import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import proposalsApi from '../../APIs/proposals';
import {
  Button,
  PageHeader,
  Pagination,
  Table,
  Tag,
  Typography,
} from '../../designComponents';
import { TableDataType } from '../../designComponents/Table';
import { VIEW_MY_JOBS_ROUTE } from '../../routes/routePaths';
import Store from '../../store';
import { RootState } from '../../store/selectors';
import theme from '../../theme';
import { humanReadableDate, requestAPI } from '../../utils';
import { MyParamsType } from '../Building';
import { MyJobsContainerStyled } from './style';

type ParamsType = {
  page: number;
  limit: number;
};
const MyJobs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const pagination = useSelector(
    (state: RootState) => state.acceptedProposals.pagination,
  );
  const myjobs = useSelector(
    (state: RootState) => state.acceptedProposals.data,
  );

  const hanldeAcceptedProposals = async (myParams: MyParamsType) => {
    try {
      await requestAPI(
        proposalsApi.getAcceptedProposals({
          page: myParams.page,
          limit: myParams.limit,
          search: myParams.search ? myParams.search : '',
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const debouncedFunction = useCallback(
    debounce((myParams) => hanldeAcceptedProposals(myParams), 500),
    [],
  );

  useEffect(() => {
    hanldeAcceptedProposals(pagination);
  }, []);

  const tableData: TableDataType = {
    headings: [
      { key: 'date', label: 'Date' },
      { key: 'title', label: 'Job Title' },
      {
        key: 'name',
        label: 'Building Name',
      },
      { key: 'address', label: 'Address of Property' },
      {
        key: 'status',
        label: 'Status',
      },
      {
        key: 'actions',
        label: 'Action',
      },
    ],
    body: Array.isArray(myjobs)
      ? myjobs.map((myjob) => {
        return {
          id: myjob.acceptedProposal._id,
          date: humanReadableDate(myjob.updatedAt),
          title: myjob.technicianJob.jobTitle,
          name: myjob?.building?.buildingName,
          address: myjob.building.buildingAddress,
          status: <Tag type="success">{myjob?.technicianJob?.status}</Tag>,
          actions: (
              <Button
                variant="secondary"
                onClick={() => {
                  navigate(
                    VIEW_MY_JOBS_ROUTE.replace(':myJobProposalId', myjob._id),
                  );
                }}
                style={{
                  backgroundColor: theme.colors.blueLight,
                  border: `1px solid ${theme.colors.blueLight}`,
                }}
              >
                View Details
              </Button>
          ),
        };
      })
      : [],
  };

  const handlePagination = (updatedParams: ParamsType) => {
    hanldeAcceptedProposals(updatedParams);
    dispatch(
      Store.Actions.update('acceptedProposals.pagination', {
        ...pagination,
        ...updatedParams,
      }),
    );
  };

  const handleOnSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debouncedFunction({ ...pagination, page: 1, search: value });
  };

  return (
    <MyJobsContainerStyled>
      <PageHeader
        pageTitle={<Typography variant="title">My job Listing</Typography>}
        handleOnSearchTextChange={handleOnSearchTextChange}
        search={search}
      />

      <div className="table-container">
        <Table tableData={tableData} maxWidthForCardView="800px" />
      </div>

      <Pagination
        activePage={pagination.page}
        handlePagination={handlePagination}
        limit={pagination.limit}
        totalCount={pagination.totalData || 1}
      />
    </MyJobsContainerStyled>
  );
};

export default MyJobs;
